/**
 * L2: which resources fire the most alerts, over the persisted alert history.
 *
 * The alert list one panel up is a snapshot — it says what is firing now and
 * nothing about the resource that has fired forty times this month. This panel
 * is the other question, and it is answered entirely from `alert_event`, the
 * table the poller and the webhook ingest have been filling all along. No
 * Azure call is made for it.
 *
 * Honesty: `history` is null when the database could not be read, and that is
 * rendered as an explicit "unknown", never as an empty ranking. Alerts Azure
 * gave no target resource for are counted in the caption rather than bucketed
 * under a placeholder row. And the ranking is estate-wide — the filters above
 * narrow the live window, not the persisted history — so the caption says so
 * rather than letting a filtered page imply a filtered ranking.
 */

import { Badge, DataGrid, EmptyState, Typography } from "@norican/react";

import type { AlertResourceFrequency, CloudAlerts } from "@/lib/api";
import { formatCount, severityStatus } from "@/lib/charts/config";

import { PanelCard } from "./panel-card";
import { formatTimestamp, shortType } from "./status";

interface AlertFrequencyPanelProps {
  alerts: CloudAlerts;
}

export function AlertFrequencyPanel({ alerts }: AlertFrequencyPanelProps) {
  const history = alerts.history;

  if (history == null) {
    return (
      <PanelCard title="Fires most often" caption="Unknown">
        <Typography variant="body-sm" tone="muted">
          The persisted alert history could not be read, so how often each resource fires is unknown
          — not zero. The reason is listed under collection problems.
        </Typography>
      </PanelCard>
    );
  }

  const caption = [
    `${formatCount(history.events)} alert${history.events === 1 ? "" : "s"} recorded over ${
      history.window_days
    } days`,
    `${formatCount(history.resources)} resource${history.resources === 1 ? "" : "s"} involved`,
    history.unattributed > 0
      ? `${formatCount(history.unattributed)} named no resource, excluded from the ranking`
      : null,
    history.oldest_event_at ? `oldest recorded ${formatTimestamp(history.oldest_event_at)}` : null,
    "estate-wide, unaffected by the filters above",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <PanelCard
      title="Fires most often"
      caption={caption}
      padding={history.top_resources.length === 0 ? "md" : "none"}
    >
      {history.top_resources.length === 0 ? (
        <EmptyState
          size="sm"
          title="No alert has been recorded against a resource yet"
          description={`Nothing fired in the last ${history.window_days} days that named a target resource.`}
        />
      ) : (
        <DataGrid<AlertResourceFrequency>
          data={history.top_resources}
          getRowId={(row) => row.resource_id ?? row.resource_name}
          getRowSearchText={(row) => `${row.resource_name} ${row.subscription_name}`}
          density="compact"
          aria-label="Resources ranked by how many alerts they fired"
          columns={[
            {
              id: "alerts",
              header: "Alerts",
              accessor: (row) => row.alerts,
              sortable: true,
              align: "end",
              cell: (row) => (
                <span className="text-fg font-medium tabular-nums">{formatCount(row.alerts)}</span>
              ),
            },
            {
              id: "resource",
              header: "Resource",
              accessor: (row) => row.resource_name,
              sortable: true,
              cell: (row) => (
                <div className="flex flex-col">
                  <span className="text-fg font-medium">{row.resource_name}</span>
                  {row.resource_type && (
                    <span className="text-fg-muted text-xs">{shortType(row.resource_type)}</span>
                  )}
                </div>
              ),
            },
            {
              id: "worst",
              header: "Worst severity",
              accessor: (row) => row.worst_severity ?? "",
              sortable: true,
              cell: (row) =>
                row.worst_severity ? (
                  <Badge tone={severityStatus(row.worst_severity).tone} variant="subtle" size="xs">
                    {severityStatus(row.worst_severity).short}
                  </Badge>
                ) : (
                  <span className="text-fg-muted text-sm">—</span>
                ),
            },
            {
              id: "firing",
              header: "Still firing",
              accessor: (row) => row.still_firing,
              sortable: true,
              align: "end",
              cell: (row) => (
                <span className="text-fg-muted text-sm tabular-nums">
                  {formatCount(row.still_firing)}
                </span>
              ),
            },
            {
              id: "subscription",
              header: "Subscription",
              accessor: (row) => row.subscription_name,
              sortable: true,
              cell: (row) => <span className="text-fg-muted text-sm">{row.subscription_name}</span>,
            },
            {
              id: "last",
              header: "Last fired",
              accessor: (row) => row.last_fired_at ?? "",
              sortable: true,
              cell: (row) => (
                <span className="text-fg-muted text-xs tabular-nums">
                  {formatTimestamp(row.last_fired_at)}
                </span>
              ),
            },
          ]}
        />
      )}
    </PanelCard>
  );
}
